/**
 * AdminPage - Main route component for admin functionality
 *
 * Serves as the entry point for the admin application within the unified app.
 * Handles routing for user management features with proper nested routing.
 *
 * Requirements: 1.3, 2.5
 */

import React, { createContext, useContext, useState, useCallback } from 'react';
import { Routes, Route } from 'react-router-dom';
import { AdminDashboard } from './pages/AdminDashboard';
import { UserList } from './pages/UserList';
import { UserCreate } from './pages/UserCreate';
import { UserEdit } from './pages/UserEdit';
import { FailureStats } from './pages/FailureStats';
import { QueryLogs } from './pages/QueryLogs';

// Create a context for managing user list refresh
interface AdminContextType {
  refreshUserList: () => void;
  refreshTrigger: number;
}

const AdminContext = createContext<AdminContextType | undefined>(undefined);

export const useAdminContext = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdminContext must be used within AdminPage');
  }
  return context;
};

export interface AdminPageProps {}

/**
 * Main admin route component for the unified application
 *
 * Provides routing for all admin functionality including user management
 * and system failure statistics dashboard.
 * Integrates with the unified app's authentication and theming system.
 *
 * Routes:
 * - /admin -> AdminDashboard (landing page with navigation cards)
 * - /admin/users -> UserList
 * - /admin/failure-stats -> FailureStats (system failure statistics dashboard)
 * - /admin/users/new -> UserCreate
 * - /admin/users/:userId/edit -> UserEdit
 */
export const AdminPage: React.FC<AdminPageProps> = () => {
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const refreshUserList = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
  }, []);

  const contextValue: AdminContextType = {
    refreshUserList,
    refreshTrigger,
  };

  return (
    <AdminContext.Provider value={contextValue}>
      <Routes>
        <Route path="/" element={<AdminDashboard />} />
        <Route path="/users" element={<UserList />} />
        <Route path="/failure-stats" element={<FailureStats />} />
        <Route path="/query-logs" element={<QueryLogs />} />
        <Route path="/users/new" element={<UserCreate />} />
        <Route path="/users/:userId/edit" element={<UserEdit />} />
      </Routes>
    </AdminContext.Provider>
  );
};
